import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Save, Trash2, MapPin, Tag as TagIcon, X, Loader2, Calendar } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type SitePhoto = {
  id: number;
  projectId: number;
  url: string;
  area: string | null;
  note: string | null;
  tags: string[];
  takenAt: string | null;
  createdAt: string;
  latitude: number | null;
  longitude: number | null;
};

const PHOTOS_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "photos"];
const PHOTO_QUERY_KEY = (photoId: number) => ["field", "photos", photoId];

export default function FieldPhotoDetail() {
  const { id } = useParams();
  const photoId = parseInt(id ?? "0", 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const photoQuery = useQuery<SitePhoto>({
    queryKey: PHOTO_QUERY_KEY(photoId),
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/field/photos/${photoId}`);
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!photoId,
  });

  const photo = photoQuery.data;
  usePageTitle(photo?.area ?? "Photo");

  const [area, setArea] = useState("");
  const [note, setNote] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [dirty, setDirty] = useState(false);
  const initializedFor = useRef<number | null>(null);

  useEffect(() => {
    if (photo && initializedFor.current !== photoId) {
      initializedFor.current = photoId;
      setArea(photo.area ?? "");
      setNote(photo.note ?? "");
      setTags(photo.tags ?? []);
      setDirty(false);
    }
  }, [photo, photoId]);

  const updateMutation = useMutation({
    mutationFn: async (data: { area: string | null; note: string | null; tags: string[] }) => {
      const res = await fetch(`${BASE}/api/field/photos/${photoId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      return res.json();
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(PHOTO_QUERY_KEY(photoId), updated);
      if (photo) queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY(photo.projectId) });
      toast({ title: "Photo details saved" });
      setDirty(false);
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/field/photos/${photoId}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
    },
    onSuccess: () => {
      if (photo) queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY(photo.projectId) });
      toast({ title: "Photo deleted" });
      setLocation(photo ? `/field/${photo.projectId}` : "/field");
    },
    onError: () => toast({ title: "Failed to delete photo", variant: "destructive" }),
  });

  const addTag = () => {
    const t = newTag.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setNewTag("");
      setDirty(true);
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
    setDirty(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ area: area || null, note: note || null, tags });
  };

  if (photoQuery.isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
        <Loader2 style={{ width: "2rem", height: "2rem" }} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!photo) {
    return <div style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>Photo not found.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0", minHeight: "calc(100vh - 4rem)" }}>
      {/* Back link */}
      <div style={{ marginBottom: "1rem" }}>
        <Link href={`/field/${photo.projectId}`}>
          <button style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", background: "none", border: "none", cursor: "pointer", padding: 0 }}
            className="hover:text-foreground transition-colors">
            <ArrowLeft style={{ width: "1rem", height: "1rem" }} />
            Back to project
          </button>
        </Link>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.25rem" }} className="lg:grid-cols-[1fr_380px]">
        {/* Image */}
        <div style={{ backgroundColor: "#111827", borderRadius: "0.75rem", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "300px" }}>
          <img
            src={photo.url}
            alt={photo.area ?? "Site photo"}
            style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
          />
        </div>

        {/* Metadata panel */}
        <div style={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid hsl(var(--border))", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: "1rem" }}>Photo Details</div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", color: "hsl(var(--muted-foreground))", marginTop: "0.2rem" }}>
                <Calendar style={{ width: "0.75rem", height: "0.75rem" }} />
                {photo.takenAt ? new Date(photo.takenAt).toLocaleString() : new Date(photo.createdAt).toLocaleString()}
              </div>
              {photo.latitude != null && photo.longitude != null && (
                <a
                  href={`https://maps.google.com/?q=${photo.latitude},${photo.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.8rem", color: "hsl(var(--primary))", marginTop: "0.2rem", textDecoration: "none" }}
                  className="hover:underline"
                >
                  <MapPin style={{ width: "0.75rem", height: "0.75rem", flexShrink: 0 }} />
                  {photo.latitude.toFixed(5)}, {photo.longitude.toFixed(5)}
                </a>
              )}
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" style={{ color: "hsl(var(--destructive))" }}>
                  <Trash2 style={{ width: "1.125rem", height: "1.125rem" }} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
                  <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate()}
                    style={{ backgroundColor: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}
                  >
                    {deleteMutation.isPending ? <Loader2 style={{ width: "0.875rem", height: "0.875rem" }} className="animate-spin mr-1" /> : null}
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* Fields */}
          <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            {/* Area */}
            <div>
              <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", fontWeight: 700 }}>Area</Label>
              <div style={{ position: "relative", marginTop: "0.375rem" }}>
                <MapPin style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", width: "0.875rem", height: "0.875rem", color: "hsl(var(--muted-foreground))" }} />
                <Input
                  value={area}
                  onChange={(e) => { setArea(e.target.value); setDirty(true); }}
                  placeholder="e.g. Kitchen, Roof, Basement"
                  style={{ paddingLeft: "2.25rem" }}
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", fontWeight: 700 }}>Tags</Label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", marginTop: "0.375rem", marginBottom: "0.5rem" }}>
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" style={{ display: "flex", alignItems: "center", gap: "0.25rem", paddingRight: "0.375rem" }}>
                    <TagIcon style={{ width: "0.625rem", height: "0.625rem", opacity: 0.6 }} />
                    {tag}
                    <button onClick={() => removeTag(tag)} style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", opacity: 0.7 }}>
                      <X style={{ width: "0.625rem", height: "0.625rem" }} />
                    </button>
                  </Badge>
                ))}
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Add a tag…"
                  style={{ flex: 1 }}
                />
                <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
              </div>
            </div>

            {/* Note */}
            <div>
              <Label style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))", fontWeight: 700 }}>Note</Label>
              <Textarea
                value={note}
                onChange={(e) => { setNote(e.target.value); setDirty(true); }}
                placeholder="Add details about this photo…"
                style={{ marginTop: "0.375rem", minHeight: "6rem", resize: "vertical" }}
              />
            </div>
          </div>

          {/* Save button */}
          <div style={{ padding: "1rem 1.25rem", borderTop: "1px solid hsl(var(--border))" }}>
            <Button onClick={handleSave} disabled={updateMutation.isPending || !dirty} style={{ width: "100%" }}>
              {updateMutation.isPending ? <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" /> : <Save style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
